(() => {
  if (window.__liliumMobileCarouselBooted) return;
  window.__liliumMobileCarouselBooted = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class LiliumMobileCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.prev = root.querySelector('[data-carousel-prev]');
      this.next = root.querySelector('[data-carousel-next]');
      this.status = root.querySelector('[data-carousel-status]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.index = 0;
      this.timer = null;
      this.resumeTimer = null;
      this.isVisible = true;
      this.autoplay = root.dataset.autoplay === 'true';
      this.interval = Math.max(5000, Number(root.dataset.interval) || 6000);

      if (!this.track || this.slides.length < 2) {
        root.classList.add('lilium-carousel--single');
        return;
      }

      this.onPrev = () => this.goTo(this.index - 1, true);
      this.onNext = () => this.goTo(this.index + 1, true);
      this.onScroll = this.debounce(() => this.syncIndex(), 80);
      this.onInteraction = () => this.pauseTemporarily();
      this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.start();
      };

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onInteraction, { passive: true });
      this.track.addEventListener('touchstart', this.onInteraction, { passive: true });
      this.root.addEventListener('mouseenter', () => this.stop());
      this.root.addEventListener('mouseleave', () => this.start());
      this.root.addEventListener('focusin', () => this.stop());
      this.root.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      this.observer = new IntersectionObserver(
        (entries) => {
          this.isVisible = entries[0]?.isIntersecting ?? true;
          if (this.isVisible) this.start();
          else this.stop();
        },
        { threshold: 0.35 }
      );
      this.observer.observe(root);

      mobileQuery.addEventListener?.('change', () => {
        this.index = 0;
        this.goTo(0, false);
        this.start();
      });

      this.updateStatus();
      this.start();
    }

    debounce(fn, delay) {
      let id;
      return (...args) => {
        window.clearTimeout(id);
        id = window.setTimeout(() => fn(...args), delay);
      };
    }

    syncIndex() {
      if (!mobileQuery.matches) return;
      const left = this.track.scrollLeft;
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const current = Math.abs(slide.offsetLeft - left);
        if (current < distance) {
          closest = index;
          distance = current;
        }
      });
      this.index = closest;
      this.updateStatus();
    }

    goTo(index, userInitiated = false) {
      if (!mobileQuery.matches || !this.slides.length) return;
      const total = this.slides.length;
      this.index = (index + total) % total;
      const slide = this.slides[this.index];
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.updateStatus();
      if (userInitiated) this.pauseTemporarily();
    }

    updateStatus() {
      if (this.status) this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.prev?.setAttribute('aria-label', `Previous item. Showing ${this.index + 1} of ${this.slides.length}`);
      this.next?.setAttribute('aria-label', `Next item. Showing ${this.index + 1} of ${this.slides.length}`);
    }

    pauseTemporarily() {
      this.stop();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.start(), Math.max(4000, this.interval * 3));
    }

    start() {
      this.stop();
      if (!this.autoplay || !mobileQuery.matches || reduceMotion.matches || !this.isVisible || document.hidden) return;
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-lilium-carousel]').forEach((root) => {
      if (!root.__liliumCarousel) root.__liliumCarousel = new LiliumMobileCarousel(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
